import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DialogContent } from "../../components/dialog/Dialog";
import { useDialog } from "../../components/dialog/DialogContext";
import { Direction } from "../../constants/Direction";
import { FILTER_DATE, FILTER_TIME } from "../../constants/filter-types";
import { exportReport } from "../../exporters/ExportHelper";
import { ButtonTypes } from "../../models/ButtonTypes";
import { CellWarning, CustomDeleteKey, CustomRowModifier, CustomRowModifierWithRange } from "../../models/CustomRowAction";
import { ExportableReport } from "../../models/ExportableReport";
import { Filter } from "../../models/Filter";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { PaginationModel } from "../../models/Pagination";
import { SortBy } from "../../models/SortBy";
import { UserPreferenceFilter } from "../../models/UserPreferenceFilter";
import { SearchTerm } from "../../models/search/SearchTerm";
import { DeleteEntityResult } from "../../models/services/DeleteEntityResult";
import { TableColumn } from "../../models/table/TableColumn";
import { TableLegendItem } from "../../models/table/TableLegendItem";
import { deleteSettingData } from "../../services/settingDataService";
import { deleteUserPreference, getSettingDataForUserLogin, getUserColumnsByView, getUserFilterPreference, storeSettingData, storeUserPreferenceColumn, storeUserPreferenceFilters } from "../../services/userPreferenceService";
import { getErrorMessage, showError } from "../../services/utilities/ServiceError";
import { IAppState } from "../../store/types";
import { getDateWithFullTime, processSelectedDates } from "../../utilities/DateUtils";
import { TranslationKeys } from "../TranslationKeys";
import { useLocale } from "../localeContext";
import { FilterContext } from "./FilterContext";
import { LegendContext } from "./LegendContext";
import { PaginationContext, emptyPaginationContextValue } from "./PaginationContext";
import { SearchContext } from "./SearchContext";
import { SortContext } from "./SortContext";
import { TableContext } from "./TableContext";

interface FilterPreference {
    filterId;
    preferenceId;
}

interface UserPreferenceKey {
    userLoginId;
    currentMenuDetailId;
    reportId;
    settingId;
}

export interface TableContextProviderValueType {
    children;
    tableDescriptor?: TableColumn[];
    searchDescriptor?: SearchTerm[];
    dataMethod?: (query?: ListQuery) => Promise<ListData>;
    legendItems?: TableLegendItem[];
    initialiser?: (setDetails: (userLoginId, currentMenuDetail, customFilters?) => void) => void;
    useTable?: boolean;
    isMultiSelect?: boolean;
    usePagination?: boolean;
    useFilter?: boolean;
    useSearch?: boolean;
    useLegend?: boolean;
    useSort?: boolean;
    legendKey?: string;
    legendLabel?: string;
    /**
     * This function is mainly used if you want to refresh the full data by calling a new data method
     * that was supplied.  
     *
     * i.e. For views that populate that using websockets and you want to refresh the data on demand.
     *
     * @param manualDataRefresh this will pull new data using the given data method
     * @returns 
     */
    manualDataRefresh?: (manualDataRefresh: (newDataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => void;
    deleteMethod?: (id: string, isCustomDeleteKey?: boolean) => Promise<DeleteEntityResult>;
    editMethod?: (id: string) => void;
    /**
     * This function is close manualDataRefresh, the difference is that this function updates the exisiting table data
     * whereas manualDataRefresh gets new data from the dataMethod 
     *
     * @param tableDataUpdate this will be the current dataset shown in the table
     * @returns 
     */
    tableDataUpdate?: (tableDataUpdate: (updateTableMethod: (tableData: any[]) => any[]) => void) => void;
    swapMethod?: (item: any) => void;
    maxPageSize?: number;
    disableSearch?: boolean;
    customDeleteKey?: CustomDeleteKey;
    alwaysSelectedAndDisabled?: string[];
    downloadMethod?: (item: any) => void;
    lookup?: Lookup[];
    customDelete?: CustomRowModifier,
    customDeleteWithRange?: CustomRowModifierWithRange,
    customDeleteMsgTrnKey?: string,
    customEditWithRange?: CustomRowModifierWithRange,
    customEdit?: CustomRowModifier,
    customDownload?: CustomRowModifier,
    canExportReport?: boolean,
    manuallyMuster?: (rowData: any) => void;
    customMusteringWithRange?: CustomRowModifierWithRange;
    getOriginalStaticFilter?: (filterKey: string) => Promise<Filter>;
    onMoveUpOrDown?: (item:any, rowIndex: number, direction: 'up' | 'down') => void;
}

export const TableContextProvider: FC<TableContextProviderValueType> = ({
    children,
    tableDescriptor: newTableDescriptor = new Array(0),
    searchDescriptor: newSearchDescriptor = new Array(0),
    dataMethod: newDataMethod = () => { return new Promise((_, reject) => { reject({ rows: new Array(0), pagination: emptyPaginationContextValue.pagination }) }) },
    legendItems = [],
    initialiser = (setDetails) => { setDetails(undefined, undefined, []) },
    useTable = false,
    isMultiSelect = true,
    usePagination = false,
    useFilter = false,
    useSearch = false,
    useLegend = false,
    useSort = false,
    legendKey = undefined,
    legendLabel = undefined,
    manualDataRefresh = undefined,
    deleteMethod = undefined,
    editMethod = undefined,
    tableDataUpdate = undefined,
    swapMethod = undefined,
    maxPageSize = undefined,
    disableSearch = false,
    customDeleteKey = undefined,
    alwaysSelectedAndDisabled = [],
    downloadMethod = undefined,
    lookup = [],
    customDelete = undefined,
    customDeleteWithRange = undefined,
    customDeleteMsgTrnKey = undefined,
    customEdit = undefined,
    customEditWithRange = undefined,
    customDownload = undefined,
    canExportReport = true,
    manuallyMuster = undefined,
    customMusteringWithRange = undefined,
    getOriginalStaticFilter = undefined,
    onMoveUpOrDown = undefined
}) => {

    const generatePaginationWithNewMaxSize = () => {
        if (maxPageSize) {
            const newPagination = {
                maxPageSize: maxPageSize,
                pageNumber: 1,
                totalCount: 0,
                totalPages: 1,
                sizes: [maxPageSize, 50, 100],
                pages: [1]
            }

            return newPagination;
        }
        return emptyPaginationContextValue.pagination;
    }

    const [tableData, setTableData] = useState(new Array(0));
    const [dataMethod, setDataMethod] = useState<(query?: ListQuery) => Promise<ListData>>(() => newDataMethod);
    const [pagination, setPagination] = useState<PaginationModel>(generatePaginationWithNewMaxSize());
    const [sort] = useState<SortBy>();
    const [filters, setFilters] = useState<Filter[]>(new Array(0));
    const [searchTerm] = useState<SearchTerm>({ accessor: newSearchDescriptor[0]?.searchOnName ?? '', translationKey: newSearchDescriptor[0]?.translationKey ?? '' });
    const [tableDescriptor, setTableDescriptor] = useState<TableColumn[]>(newTableDescriptor);
    const [searchDescriptor] = useState<TableColumn[]>(newSearchDescriptor);
    const [userPreference, setUserPreferenceColumn] = useState<any>(newSearchDescriptor);
    const initialQuery = {
        filters: new Array(0),
        goToPage: pagination.pageNumber,
        pageSize: maxPageSize ? maxPageSize : pagination.maxPageSize,
        searchTerm: searchTerm,
        sortBy: sort
    };
    const [query] = useState<ListQuery>(initialQuery);
    const [userPreferenceKey, setUserPreferenceKey] = useState<UserPreferenceKey>();
    const { translate } = useLocale();
    const [busy, setBusy] = useState<boolean>(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const dialog = useDialog();
    const [userColumnByViewData, setUserColumnByViewData] = useState<any>();

    useEffect(() => {
        getUserColumn();
    }, [userPreferenceKey])

    const getUserColumn = async () => {
        if (userPreferenceKey?.currentMenuDetailId && userColumnByViewData !== undefined) {
            let newShownColumns = [] as any;
            try {
                const userPreferenceColumnIds = userColumnByViewData.map((item) => item.columnId)
                tableDescriptor.map((columnId) => {
                    if (userPreferenceColumnIds.includes(columnId.columnId || columnId.isDefault === true)) {
                        newShownColumns.push({ ...columnId, active: true })
                    } else {
                        newShownColumns.push({ ...columnId, active: false })
                    }
                })
                setUserPreferenceColumn({
                    currentMenuDetail: userPreferenceKey?.currentMenuDetailId,
                    userLoginId: userPreferenceKey?.userLoginId,
                    userPreferenceColumnIds: userColumnByViewData.map((item) => item.columnId),
                    userPreferenceColumms: userColumnByViewData
                });
            } catch (error) {
                showError(error, toast.warn);
            }
            const doesNotHavePreferences = newShownColumns.every((item) => { return item.active === false });
            !doesNotHavePreferences && setTableDescriptor(newShownColumns);
        }
    }

    useEffect(() => {
        handleMenuSelection(tableDescriptor)
    }, [tableDescriptor])

    // fancy callback to inject user and menu details into user preference initialiser method.
    const setDetails = async (userLoginId, currentMenuDetailId, customFilter: Filter[], reportId?: string, staticFilterKeys?: string[], settingId?: string) => {
        try {
            if (userLoginId && currentMenuDetailId) {
                if (customFilter) {
                    filters.push(...customFilter)
                }

                const filterPreferences = await getUserFilterPreference(userLoginId, currentMenuDetailId, reportId, settingId)
                // create a preference-filter mapping facilitating storage and removal on update.
                const newUserFilterPreferences = filterPreferences.map(newFilter => {
                    return { filterId: newFilter.filterId, preferenceId: newFilter.preferenceId };
                })
                const newFilters = filterPreferences.map(newFilter => {
                    // first find the column definition for each filter
                    const columnDescriptor = tableDescriptor.find(descriptor => descriptor.filterId === newFilter.filterId);
                    if (columnDescriptor && columnDescriptor.searchOnName) {
                        const filter: Filter = {
                            key: columnDescriptor.searchOnName,
                            label: translate.byKey(TranslationKeys[columnDescriptor.translationKey]) ?? columnDescriptor.heading,
                            options: new Array(0),
                            selected: FILTER_DATE ? processSelectedDates(newFilter.selected, translate) : newFilter.selected
                        }
                        return filter;
                    }
                    return undefined;
                }).filter(filter => filter !== undefined && filter.selected.length !== 0);

                userFilterPreferences.push(...newUserFilterPreferences);
                filters.push(...newFilters as Filter[]);

                const responseUserColumnsByView = await getUserColumnsByView(userLoginId, currentMenuDetailId, reportId, settingId);
                setUserColumnByViewData(responseUserColumnsByView);
                setUserPreferenceKey({ userLoginId, currentMenuDetailId, reportId, settingId });

                const userPreferenceColumnIds = responseUserColumnsByView.map((item) => item.columnId);
                if (userPreferenceColumnIds?.length > 0) {
                    const newTableDescriptor = tableDescriptor.map((descriptor: TableColumn) => {
                        if (userPreferenceColumnIds.includes(descriptor.columnId) || descriptor.isDefault === true) {
                            return { ...descriptor, active: true };
                        }
                        else {
                            return { ...descriptor, active: false };
                        }
                    });
                    setTableDescriptor(newTableDescriptor);
                }
            }
            // legend filter addition for all selected initially
            if (legendKey) {
                const legendFilter = createLegendFilters();
                filters.push(legendFilter);
            }

            // hanlde static filters
            if (staticFilterKeys) {
                staticFilterKeys.forEach(async (key) => {
                    const filter = filters.find((filter) => filter.key === key);

                    // mark static filters as static
                    if (filter) {
                        filter.isStatic = true;
                    }
                    else {
                        // if the filter is not already in the list, add it
                        const originalCustomFilter = getOriginalStaticFilter && await getOriginalStaticFilter(key);
                        if (originalCustomFilter) {
                            filters.push(originalCustomFilter);
                        }
                    }
                })
            }
            setFilters([...filters]);
            query.filters = filters;
            refreshData(query);
        } catch (error) {
            toast.warn(translate.byKey(TranslationKeys.loadTopicFailed))
        }
    };

    // pull user preferences for filters and columns on startup
    useEffect(() => {
        // fancy callback injection
        initialiser(setDetails);
        if (manualDataRefresh) {
            manualDataRefresh((newDataMethod?: (query?: ListQuery) => Promise<ListData>) => {
                refreshData(query, newDataMethod);
            });
        }
    }, []);

    useEffect(() => {
        if (tableDataUpdate) {
            tableDataUpdate((updateTableMethod: (tableData: any[]) => any[]) => {
                const rows = updateTableMethod(tableData);
                // only update the table data if the data has changed
                setTableData(prev => prev === rows ? prev : [...rows]);
            })
        }
    }, [tableData])


    // --> start sort
    const updateDirection = (columnId) => {
        const column = tableDescriptor.find(column => column.id === columnId);
        if (column) {
            if (column.sortDirection === Direction.ascending) {
                column.sortDirection = Direction.descending;
            }
            else {
                column.sortDirection = Direction.ascending;
            }
            const newSort: SortBy = { columnId: column.id, direction: column.sortDirection, columnName: column.sortByName };
            query.sortBy = newSort;
            refreshData(query)
        }
    }
    // <-- end sort

    // --> start legend
    const createLegendFilters = () => {
        const newFilter: Filter = {
            key: legendKey ?? '',
            label: legendLabel ?? '',
            showFilterChips: false,
            options: legendItems.map(legend => {
                return { value: `${legend.value}`, label: legend.label }
            }),
            selected: legendItems.map(legend => {
                return { value: `${legend.value}`, label: legend.label }
            })
        }
        return newFilter;
    }

    const onLegendFilterChange = (selectedLegend: TableLegendItem) => {
        const legend: TableLegendItem | undefined = legendItems.find(legend => legend.value === selectedLegend.value);
        const legendFilter = filters.find(filter => filter.key === legendKey);

        // if the legend or legend filter is not found, return
        if (!legend || !legendFilter) {
            return;
        }

        const selectedFilter = legendFilter.selected.find(option => option.value === legend.value);
        if (selectedFilter) {
            removeFilter(legendFilter, selectedFilter, undefined);
            legend.isSelected = false;
        }
        else {
            updateExistingFilter(legend.value, { value: legend.value, label: legend.label }, legendFilter, undefined);
            legend.isSelected = true;
        }

        const countSelected = legendItems.filter(item => item.isSelected).length
        if (countSelected === 1) {
            const onlySelected = legendItems.find(item => item.isSelected);
            if (onlySelected) {
                onlySelected.disabled = true;
            }
        }
        else {
            legendItems.forEach(item => {
                item.disabled = false;
            });
        }
    }
    // <-- end legend

    // --> start search
    const updateSearchTerm = (newSearchTerm) => {
        searchTerm.value = newSearchTerm;
        query.goToPage = 1;

        query.searchTerm = searchTerm;
        refreshData(query);
    }
    const updateSearchColumn = (newSearchColumn) => {
        searchTerm.accessor = newSearchColumn;

        query.searchTerm = searchTerm;
        refreshData(query);
    }
    // <-- end search

    // --> start filters
    const [userFilterPreferences, setUserFilterPreferences] = useState<FilterPreference[]>(new Array(0));

    const updateNonColumnFilter = async (newSelectedOption: MultiSelectOption, filterKey: string, filterLabel: string, isSingleSelect: boolean, options: MultiSelectOption[] = []): Promise<void> => {
        // Check we already have this filter applied
        const filter = filters.find(filter => filter.key === filterKey);

        // Update the filter if it exists
        if (filter) {
            const selectedFilter = filter.selected.find((selectedFilter) => selectedFilter.value === newSelectedOption.value);

            // The option is ready applied, so remove it
            if (selectedFilter && !isSingleSelect) {
                // Remove the selected filter
                filter.selected.splice(filter.selected.indexOf(selectedFilter), 1);

                //Close the filter if there are no selected options
                if (filter.selected.length < 1) {
                    await closeFilter(filter.key)
                }
                return;
            }

            // Update the filter with the new selected option
            const index = filters.indexOf(filter);
            const selected = isSingleSelect ? [] : filter.selected;
            filters[index] = { ...filter, selected: [...selected, newSelectedOption] };
            setFilters([...filters]);
            query.filters = filters;
            await refreshData(query);

        }
        // Add the filter if it doesn't exist
        else {
            const newFilter: Filter = {
                key: filterKey,
                label: filterLabel,
                options: options,
                selected: [newSelectedOption]
            }

            filters.push(newFilter);
            setFilters([...filters]);
            query.goToPage = 1;
            query.filters = filters;
            await refreshData(query);
        }
    }

    const updateFilters = async (columnId, itemId, itemValue, columnKey, elementSysname): Promise<void> => {
        const filterColumn: TableColumn = newTableDescriptor.find((column: TableColumn) => {
            return columnKey ? column.searchOnName === columnKey : column.id === columnId
        });
        let newSelectedFilter: MultiSelectOption = { value: itemId, label: itemValue, elementSysname };
        const filter = filters.find(filter => { return filter.key === filterColumn?.searchOnName; })
        if (filter) {
            // Wait while we do clean for single select filter
            if (lookup.find(lookup => lookup.name === filterColumn.lookupName)?.isSingleSelect) {
                await handleSingleSelectFilter(filterColumn.filterId ?? "");
                filter.selected = [];
            }

            const selectedFilter = filter.selected.find((selectedFilter) => selectedFilter.value === itemId);

            if (selectedFilter) {
                await removeFilter(filter, selectedFilter, filterColumn)
            }
            else {
                //This is an existing filter so we need the setting data id to update, because the filter already exist.
                newSelectedFilter = {
                    ...newSelectedFilter,
                    settingDataId: filter.selected.length > 0 ? filter.selected[0].settingDataId : undefined
                };

                //If this filter has filter that we need to ignore when building the filter, we need to remove them.
                filter.selected = filter.selected.filter(selected => selected.ignore !== true);

                await updateExistingFilter(itemId, newSelectedFilter, filter, filterColumn)
            }
        }
        else {
            const newFilter: Filter = {
                key: filterColumn.searchOnName as string,
                label: translate.byKey(TranslationKeys[filterColumn.translationKey]) ?? filterColumn.heading,
                options: new Array(0),
                selected: [
                    newSelectedFilter
                ]
            }
            await saveFilterPreference(filterColumn, newFilter, false);
        }
    }

    const handleMenuSelection = async (cols) => {
        try {
            let columnsToBeAdded = [] as any
            let columnsToBeRemoved = [] as any

            cols.map(col => {
                // Not all columns have a settingTypeId yet. We may also not have the userPreference.userPreferenceColumnIds
                let supportsPersist = !!col.columnId && !!userPreference && !!userPreference.userPreferenceColumnIds;

                if (supportsPersist) {
                    if (!(userPreference.userPreferenceColumnIds.includes(col.columnId)) && col.active) {
                        columnsToBeAdded.push(col)
                        userPreference.userPreferenceColumnIds.push(col.columnId);
                    } else if (userPreference.userPreferenceColumnIds.includes(col.columnId) && !col.active) {
                        columnsToBeRemoved.push(col)

                        // remove column id after deleting to avoid deleting non existing data next time you delete a column.
                        const index = userPreference.userPreferenceColumnIds.indexOf(col.columnId);
                        if (index > -1) {
                            userPreference.userPreferenceColumnIds.splice(index, 1);
                        }
                    }
                }
            })

            if (columnsToBeAdded.length > 0) {
                columnsToBeAdded.map(async item => {
                    const columnPreference = {
                        menuSettingTypeId: userPreference.currentMenuDetail,
                        columnId: item.columnId,
                        userLoginId: userPreference.userLoginId,
                        reportId: userPreferenceKey?.reportId,
                        settingId: userPreferenceKey?.settingId
                    }

                    storeUserPreferenceColumn(columnPreference).catch((error) => {
                        throw error;
                    })
                })
            }

            if (columnsToBeRemoved.length > 0) {

                // select only those ids that exist in columnsToBeRemoved
                const idsToRemove = columnsToBeRemoved.map((column) => {
                    return userPreference?.userPreferenceColumms?.find(item => item.columnId === column.columnId).id
                })

                for (const id of idsToRemove) {
                    await deleteUserPreference(id);
                }
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const closeFilter = async (accessor: string) => {
        const filterBeingRemoved = filters.find((filter) => filter.key === accessor)
        let newFilters = filters.filter((filter) => filter.key !== accessor)
        const columnAssociatedWithFilter = tableDescriptor.find(column => column.searchOnName === accessor)
        const preferenceToDelete = userFilterPreferences.find(filterPreference => filterPreference.filterId === columnAssociatedWithFilter?.filterId)

        if (preferenceToDelete) {
            deleteUserPreference(preferenceToDelete.preferenceId).then(() => {
                const newFilterPreferences = userFilterPreferences.filter(filterPreference => filterPreference.preferenceId !== preferenceToDelete.preferenceId);
                setUserFilterPreferences(newFilterPreferences)
            }).catch((error) => {
                showError(error, toast.warn);
            })
        }

        //If the filter is a static filter, we need to add it back to the filter list with the default selected option.
        if (filterBeingRemoved && filterBeingRemoved.isStatic) {
            const originalCustomFilter = getOriginalStaticFilter && await getOriginalStaticFilter(filterBeingRemoved.key);
            if (originalCustomFilter) {
                const index = filters.findIndex((filter) => filter.key === filterBeingRemoved.key);
                newFilters.splice(index, 0, originalCustomFilter);
            } else {
                newFilters.push({
                    key: filterBeingRemoved.key,
                    label: filterBeingRemoved.label,
                    options: [],
                    selected: [
                        {
                            label: translate.byKey(TranslationKeys.all),
                            value: '*',
                            ignore: true
                        }
                    ],
                    isCustomFilter: true,
                    isStatic: true
                });
            }
        }

        query.filters = newFilters;
        setFilters([...newFilters])
        await refreshData(query);
    }

    const buildSettingDataForUpdateFilter = (column: TableColumn, newSelectedFilter: MultiSelectOption, itemId: string, filter: Filter, preferenceId: string) => {

        if (column.filterId === FILTER_DATE || column.filterId === FILTER_TIME) {
            const settingDataId = filter.selected[0].settingDataId ? parseInt(filter.selected[0].settingDataId) : 0;

            return {
                settingDataId,
                value: newSelectedFilter.value,
                userPreference: preferenceId,
                filterSettingTypeId: column.filterId,
                eSysnameLookup: newSelectedFilter.elementSysname,
                dynamicObject: {
                    report: {
                        data: {
                            type: 'report',
                            id: userPreferenceKey?.reportId
                        }
                    }
                }
            };
        }
        let newDynamicObject = {};
        newDynamicObject[column?.settingDataName] = {
            data: {
                type: column?.settingDataName,
                id: newSelectedFilter.value
            }
        }

        return {
            value: itemId,
            userPreference: preferenceId,
            filterSettingTypeId: column?.filterId,
            dynamicObject: { ...newDynamicObject }
        }
    }

    const updateExistingFilter = async (itemId: string, newSelectedFilter: MultiSelectOption, filter: Filter, column?: TableColumn) => {
        try {
            const linkToPreference = userFilterPreferences.find((userFilterPreference) => userFilterPreference.filterId === column?.filterId)
            if (linkToPreference && column?.settingDataName) {
                const settingData = buildSettingDataForUpdateFilter(column, newSelectedFilter, itemId, filter, linkToPreference?.preferenceId);
                await storeSettingData(settingData);
            } else if (!!!linkToPreference && column?.settingDataName && filter.isCustomFilter) {
                /**
                * Custom filter are not persisted until a user decide to.
                * That is why they need to be treated as new preferences since they are not persisted
                * saveFilterPreference will handle the refresh of data so once complete terminate.
                */

                //Remove old options with new options. This make new filter
                filter.selected.splice(0, filter.selected.length, newSelectedFilter);

                //Check if it qualify to be a preference if so, change custom filter flag to false.
                if (userPreferenceKey && column && column.settingDataName && column.filterId) {
                    filter.isCustomFilter = false;
                }

                saveFilterPreference(column, filter, true);
                return;
            }

            if (column?.filterId === FILTER_DATE) {
                filter.selected = processSelectedDates([newSelectedFilter], translate);
            }
            else if (column?.filterId === FILTER_TIME) {
                filter.selected = [newSelectedFilter];
            }
            else {
                filter.selected.push(newSelectedFilter);
            }

            const newFilters = [...filters]
            setFilters(newFilters);
            query.goToPage = 1;
            query.filters = newFilters;
            refreshData(query);

            //After successful update, if this filter is a single select and have a dependencies, we need to close the dependencies when updating this filter.
            const filterLookup = lookup.find(lookup => lookup.name === column?.lookupName);

            if (filterLookup && filterLookup.isSingleSelect) {
                const dependencies = lookup.filter(lookupItem => lookupItem.filterDependentKey === filter.key);

                dependencies.forEach(async (dependentLookup) => {
                    const filter = filters.find(filter => filter.key.startsWith(dependentLookup.name));
                    if (filter && filter.selected.length > 0) {
                        filter && await closeFilter(filter?.key);
                    }
                })
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const saveFilterPreference = async (column: TableColumn, filter: Filter, isCustomFilter: boolean) => {
        try {
            const filterPreference = userFilterPreferences.find((userFilterPreference) => userFilterPreference.filterId === column?.filterId);
            let newPreference: any;

            // If the filter preference does not exist, create a new one.
            if (!!!filterPreference && userPreferenceKey && column && column.settingDataName && column.filterId) {
                const userPreferenceFilter: UserPreferenceFilter = {
                    filterId: column.filterId,
                    userLoginId: userPreferenceKey?.userLoginId,
                    currentMenuDetail: userPreferenceKey?.currentMenuDetailId,
                    filterData: filter.selected.map(selectedValue => {
                        return {
                            filterTypeName: column.settingDataName,
                            value: selectedValue.value,
                            elementSysname: selectedValue.elementSysname
                        };
                    }),
                    reportId: userPreferenceKey?.reportId,
                    settingId: userPreferenceKey?.settingId
                };

                newPreference = await storeUserPreferenceFilters(userPreferenceFilter);
                userFilterPreferences.push({ filterId: newPreference.filterId, preferenceId: newPreference.id });
            }
            // If the preference already exist only add the setting data
            else if (filterPreference && column && column.settingDataName && column.filterId) {
                filter.selected.forEach(async (selectedFilter) => {
                    const settingData = buildSettingDataForUpdateFilter(column, selectedFilter, selectedFilter.value, filter, filterPreference.preferenceId);
                    await storeSettingData(settingData);
                })
            }

            // If the preference was created update the filter to include settingDataId
            if (column.filterId && newPreference) {
                if (column.filterId === FILTER_DATE) {
                    filter.selected = processSelectedDates(filter.selected, translate);
                }

                filter.selected[0].settingDataId = newPreference.settingDataId;
            }

            // Custom filters will already exist in the filter list, so we need to update them.
            if (isCustomFilter) {
                const index = filters.findIndex((existingfilter: Filter) => existingfilter.key === filter.key);
                filters[index] = filter;
            }
            else {
                filters.push(filter);
                setFilters([...filters]);
            }

            //We do this either way because we have some view that don't support filter preference and we still need to apply the filter.
            query.goToPage = 1;
            query.filters = filters;
            refreshData(query);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const removeFilter = async (filter: Filter, selectedFilter: MultiSelectOption, column?: TableColumn) => {
        try {
            const linkToPreference = userFilterPreferences.find((userFilterPreference) => userFilterPreference.filterId === column?.filterId);

            if (linkToPreference) {
                const settingDatas = await getSettingDataForUserLogin(linkToPreference.preferenceId, linkToPreference.filterId);
                const settingDataToBeDeleted = settingDatas.find(settingData => settingData.label === selectedFilter.value);

                if (settingDataToBeDeleted) {
                    await deleteSettingData(settingDataToBeDeleted.value)
                }
            }
            const index = filters.indexOf(filter);
            filter.selected.splice(filter.selected.indexOf(selectedFilter), 1);

            if (filter.selected.length < 1) {
                closeFilter(filter.key)
            } else {
                filters[index] = filter;
                const newFilters = [...filters]
                setFilters(newFilters);
                query.filters = newFilters;
                refreshData(query);
            }
        }
        catch (error) {
            showError(error, toast.warn);
        }
    }

    /**
     * Check if the filter is a single select filter and delete the setting data associated with it.
     * @param filterId - The id of the filter that is a single select filter
     */
    const handleSingleSelectFilter = async (filterId: string) => {
        try {
            const linkToPreference = userFilterPreferences.find((userFilterPreference) => userFilterPreference.filterId === filterId)
            if (linkToPreference) {
                const settingDatas = await getSettingDataForUserLogin(linkToPreference?.preferenceId, linkToPreference?.filterId)
                for (const settingData of settingDatas) {
                    await deleteSettingData(settingData.value);
                }
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    // <-- end filters

    // --> start pagination
    const updatePageNumber = (pageNumber: number) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            query.goToPage = pageNumber;
            refreshData(query);
        }
    }

    const updatePageSize = (pageSize: number) => {
        if (pagination.sizes?.includes(pageSize)) {
            query.goToPage = 1;
            query.pageSize = pageSize;
            refreshData(query);
        }
    }
    // <-- end pagination

    const updateTableDescriptor = (newTableDescriptor) => {
        setTableDescriptor(newTableDescriptor);
    }

    const processData = (listData: ListData) => {
        setTableData(listData.rows);
        const newPages = Array.from(new Array(5), (_, index) => {
            // only slide for slidable page count
            if (index < listData.pagination.totalPages
                // slide upto the total pages but no further
                && index + listData.pagination.pageNumber - 2 <= listData.pagination.totalPages) {
                return listData.pagination.pageNumber < 4
                    // no sliding for pages 1, 2, 3
                    ? index + 1
                    // keep two pages on the left of selected page
                    : index + listData.pagination.pageNumber - 2;
            }

            // falls outside of slider
            else {
                return 0;
            }
        }).filter(page => page > 0);
        // override pagination to force re-render on the paginator
        setPagination({ ...pagination, ...listData.pagination, pages: newPages });
    }

    const refreshData = async (query: ListQuery, newDataMethod?) => {
        setBusy(true)
        try {
            if (newDataMethod) {
                setDataMethod(() => newDataMethod);
                const data: ListData = await newDataMethod(query)
                processData(data);
                return;
            }

            const data = await dataMethod(query)
            processData(data);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false)
        }
    };

    const exportData = async (event: MultiSelectOption, title: string, description: string) => {
        try {
            let page: number = 1;
            let dataRows = [];
            let dataModel;

            do {
                if (newDataMethod)
                    dataModel = await newDataMethod({
                        goToPage: page,
                        pageSize: 100,
                        sortBy: sort,
                        filters: filters,
                        searchTerm,
                    });
                dataRows = dataRows.concat(dataModel.rows);
                page++;
            } while (page <= dataModel.pagination.totalPages);

            let report: ExportableReport = {
                title: `${title}`,
                columns: tableDescriptor.filter((column) => column.active),
                rows: dataRows,
                description: translate.byKeyFormatted(TranslationKeys.reportSubTitle, { description }),
                user: user.name,
                filename: translate.byKeyFormatted(TranslationKeys.reportFilename, { title }),
                filters: filters,
                date: getDateWithFullTime(),
                searchTerm: getSearchTermAndColumnHeading(searchTerm),
                menuId: userPreferenceKey?.currentMenuDetailId,
                reportId: userPreferenceKey?.reportId
            };

            exportReport(report, event.value, translate, dialog);
        } catch (error) {
            toast.warn(translate.byKey(TranslationKeys.failedToGenerateReport));
        }
    };

    const getSearchTermAndColumnHeading = (newSearch: SearchTerm): SearchTerm => {
        let search: SearchTerm = { accessor: newSearch.accessor, translationKey: "" };

        searchDescriptor.forEach((col) => {
            if (col.searchOnName === newSearch.accessor) {
                search.value = newSearch.value;
                search.heading = translate.byKey(TranslationKeys[col.translationKey]);
            }
        });

        return search;
    };

    const findItemName = (id: string, isCustomDeleteKey?: boolean) => {
        let item: any;

        if (isCustomDeleteKey) {
            item = tableData.find(row => row[customDeleteKey?.idKey!] && row[customDeleteKey?.idKey!].toString() === id);
        } else {
            item = tableData.find(row => row.id.toString() === id);
        }

        return customDeleteKey?.nameKey ? item[customDeleteKey?.nameKey] : item.name;
    }

    const onDelete = (id: string, isCustomDeleteKey?: boolean) => {
        if (id && deleteMethod) {
            deleteMethod(id, isCustomDeleteKey)
                .then(() => {
                    toast.success(translate.byKey(TranslationKeys.deleteSuccess))
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
                });
        }
    }

    const handleDelete = (id: string, isCustomDeleteKey?: boolean) => {
        const dialogContent: DialogContent = {
            title: translate.byKeyFormatted(TranslationKeys.deleteRecordWithName, [findItemName(id, isCustomDeleteKey)]),
            message: customDeleteMsgTrnKey ? translate.byKey(customDeleteMsgTrnKey) : translate.byKey(TranslationKeys.permanentlyDeleteRecord),
            buttons: [
                {
                    id: "btn_confirmDelete",
                    onClick: () => { onDelete(id, isCustomDeleteKey) },
                    text: translate.byKey(TranslationKeys.confirm).toUpperCase(),
                    type: ButtonTypes.RedRoundedButton
                },
                {
                    id: "btn_Cancel",
                    onClick: () => { }, // Empty onClick will just close the dialog
                    text: translate.byKey(TranslationKeys.cancel),
                    type: ButtonTypes.TextButton,
                    className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                }
            ]
        }

        dialog.pushInfo(dialogContent);
    }

    const deleteItem = () => {
        if (deleteMethod === undefined) {
            return undefined;
        }

        return handleDelete;
    }

    const onShowWarning = (warningMessage: CellWarning) => {
        if (warningMessage) {
            dialog.pushInfo({
                title: warningMessage.title ?? translate.byKey(TranslationKeys.warning),
                message: warningMessage.message,
            });
        }
    }

    return <TableContext.Provider value={
        {
            isInUse: useTable,
            isMultiSelect,
            tableDescriptor,
            tableData,
            updateTableDescriptor,
            deleteItem: deleteItem(),
            exportData,
            editItem: editMethod,
            swapItem: swapMethod,
            customDeleteKey,
            alwaysSelectedAndDisabled,
            downloadItem: downloadMethod,
            busy,
            customDelete,
            customDeleteWithRange,
            customEdit,
            customEditWithRange,
            customDownload,
            canExportReport,
            manuallyMuster,
            customMusteringWithRange,
            menuId: userPreferenceKey?.currentMenuDetailId,
            onShowWarning,
            onMoveUpOrDown
        }
    }>
        <FilterContext.Provider value={{ isInUse: useFilter, filters, updateFilters, closeFilter, updateNonColumnFilter, lookup }}>
            <SortContext.Provider value={{ isInUse: useSort, tableDescriptor, updateDirection }}>
                <PaginationContext.Provider value={{ isInUse: usePagination, pagination, updatePageNumber, updatePageSize }}>
                    <LegendContext.Provider value={{ isInUse: useLegend, legendItems, onLegendFilterChange, tableLegendSelectable: legendKey ? true : false }}>
                        <SearchContext.Provider value={{ isInUse: useSearch, searchDescriptor, searchTerm, updateSearchColumn, updateSearchTerm, disableSearch }}>
                            {children}
                        </SearchContext.Provider>
                    </LegendContext.Provider>
                </PaginationContext.Provider>
            </SortContext.Provider>
        </FilterContext.Provider>
    </TableContext.Provider>
};
